/**
 * EnumColumnsSection — renders the enum-columns list inside the MetaInfo
 * manager (per-database dialog) and the global Settings → Meta Info view.
 * Each declaration is a row showing the qualified column name, the source
 * (native / check / sampled), and the cached value count with a "Details"
 * button that opens the EnumValuesDetailsDialog.
 *
 * Two subsections hang off the list, each hidden when empty: "Hidden enum
 * hints" (dismissed `▾?` glyphs, restorable) and "Reference tables" — the
 * declare-once flags that turn EVERY column referencing a table into an
 * implicit lookup enum. Those flags have no per-column row of their own, so
 * this is the only place the user can see and drop them.
 */

import { useMemo, useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { formatRelativeTime } from '../../utils/formatRelativeTime';
import { EnumValuesDetailsDialog } from './EnumValuesDetailsDialog';
import { ReferenceTablesSection } from './ReferenceTablesSection';
import { ProvenanceBadge } from './ProvenanceBadge';
import type {
  EnumColumnDeclaration,
  EnumGlyphDismissal,
  EnumValueCache,
  EnumValueSource,
  ReferenceTableDeclaration,
  TableDisplayColumn,
} from '@/types/electron';
import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';

const sourceBadge = (s: EnumValueSource): string => {
  if (s === 'native') return 'native';
  if (s === 'check-constraint') return 'check';
  if (s === 'lookup') return 'lookup';
  return 'sampled';
};

/**
 * `schema.table`, or just `table` on schemaless engines (Kusto reports `''`).
 * Presence-checked with `== null` — never `!schema` (Kusto invariant).
 */
const qualifyLookupTarget = (schema: string, table: string): string =>
  schema == null || schema === '' ? table : `${schema}.${table}`;

export type EnumColumnsSectionProps = {
  declarations: EnumColumnDeclaration[];
  valueCache: EnumValueCache[];
  onRemove: (decl: EnumColumnDeclaration) => void;
  /** Glyph-hint dismissals ("not an enum") — restorable here. */
  dismissals?: EnumGlyphDismissal[];
  onRestoreDismissal?: (d: EnumGlyphDismissal) => void;
  /** Refetch context — when present, the Details dialog shows Refresh. */
  connectionId?: string;
  database?: string;
  engine?: DatabaseEngineId;
  /** Reference-table flags (implicit lookup enums) — own subsection. */
  referenceTables?: ReferenceTableDeclaration[];
  /** Shared per-table label columns — the label a flagged table shows. */
  tableDisplayColumns?: TableDisplayColumn[];
};

function HiddenEnumHints({
  dismissals,
  onRestoreDismissal,
}: {
  dismissals: EnumGlyphDismissal[];
  onRestoreDismissal: (d: EnumGlyphDismissal) => void;
}) {
  if (dismissals.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="text-xs font-medium text-muted-foreground mb-1">
        Hidden enum hints
      </h4>
      <ul className="space-y-1">
        {dismissals.map((d) => (
          <li
            key={`${d.schema}.${d.table}.${d.column}`}
            className="flex items-center justify-between text-xs"
          >
            <span translate="no">
              {d.schema}.{d.table}.{d.column}
            </span>
            <button
              type="button"
              data-testid={`enum-dismissal-restore-${d.schema}.${d.table}.${d.column}`}
              className="text-primary underline underline-offset-2 hover:no-underline"
              onClick={() => onRestoreDismissal(d)}
            >
              Restore hint
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function EnumColumnsSection({
  declarations,
  valueCache,
  onRemove,
  dismissals = [],
  onRestoreDismissal,
  connectionId,
  database,
  engine,
  referenceTables = [],
  tableDisplayColumns = [],
}: EnumColumnsSectionProps) {
  const [detailsForId, setDetailsForId] = useState<string | null>(null);

  const cacheByKey = useMemo(() => {
    const map = new Map<string, EnumValueCache>();
    for (const e of valueCache) {
      map.set(`${e.schema}.${e.table}.${e.column}\0${e.path ?? ''}`, e);
    }
    return map;
  }, [valueCache]);

  const hiddenHints = onRestoreDismissal ? (
    <HiddenEnumHints
      dismissals={dismissals}
      onRestoreDismissal={onRestoreDismissal}
    />
  ) : null;

  // Reference-table flags are independent of the declarations list — a
  // database can have flags and zero declarations (that is the whole point of
  // declaring once at the target), so the subsection renders in both branches.
  const referenceTablesSection = (
    <ReferenceTablesSection
      referenceTables={referenceTables}
      tableDisplayColumns={tableDisplayColumns}
      connectionId={connectionId}
      database={database}
      engine={engine}
    />
  );

  if (declarations.length === 0) {
    return (
      <>
        <div className="text-xs text-muted-foreground italic">
          No enum columns declared yet.
        </div>
        {referenceTablesSection}
        {hiddenHints}
      </>
    );
  }

  const detailsDecl = detailsForId
    ? declarations.find((d) => d.id === detailsForId)
    : null;
  const detailsCache = detailsDecl
    ? cacheByKey.get(
        `${detailsDecl.schema}.${detailsDecl.table}.${detailsDecl.column}\0${detailsDecl.path ?? ''}`,
      )
    : undefined;

  return (
    <>
      {declarations.map((d) => {
        const cache = cacheByKey.get(
          `${d.schema}.${d.table}.${d.column}\0${d.path ?? ''}`,
        );
        const valueCount = cache?.values.length ?? 0;
        return (
          <div
            key={d.id}
            className="flex flex-col gap-1 border rounded p-2"
            data-testid={`metainfo-enum-row-${d.id}`}
          >
            <div className="flex items-center justify-between gap-2 text-xs">
              <span translate="no" className="font-mono truncate min-w-0">
                {d.schema}.{d.table}.{d.column}
                {d.path ? ` · ${d.path}` : ''}
              </span>
              <div className="flex items-center gap-1 shrink-0">
                <ProvenanceBadge provenance={d.provenance} />
                <span
                  className="rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
                  data-testid={`metainfo-enum-source-${d.id}`}
                >
                  {sourceBadge(d.source)}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setDetailsForId(d.id)}
                  className="h-6 px-2 text-[11px]"
                  data-testid={`metainfo-enum-details-${d.id}`}
                >
                  Details
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => onRemove(d)}
                  aria-label="Remove enum-column declaration"
                  title="Remove enum-column declaration"
                  className="h-6 w-6 text-destructive"
                  data-testid={`metainfo-enum-remove-${d.id}`}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
            {d.source === 'lookup' && d.lookup && (
              <div
                className="text-[10px] text-muted-foreground"
                data-testid={`metainfo-enum-lookup-target-${d.id}`}
              >
                values from{' '}
                <span className="font-mono" translate="no">
                  {qualifyLookupTarget(d.lookup.toSchema, d.lookup.toTable)}
                </span>
              </div>
            )}
            <div
              className="text-[10px] text-muted-foreground"
              data-testid={`metainfo-enum-cache-summary-${d.id}`}
            >
              {cache ? (
                <span>
                  {valueCount} value{valueCount === 1 ? '' : 's'}
                  {cache.truncated ? ' · truncated' : ''} · scanned{' '}
                  {formatRelativeTime(cache.sampledAt)}
                </span>
              ) : (
                <span className="italic">No values cached yet</span>
              )}
            </div>
          </div>
        );
      })}
      {detailsDecl && (
        <EnumValuesDetailsDialog
          open
          onOpenChange={(o) => {
            if (!o) setDetailsForId(null);
          }}
          schema={detailsDecl.schema}
          table={detailsDecl.table}
          column={detailsDecl.column}
          cache={detailsCache}
          declaredSource={detailsDecl.source}
          connectionId={connectionId}
          database={database}
          engine={engine}
          onRemoveDeclaration={() => {
            const decl = detailsDecl;
            setDetailsForId(null);
            onRemove(decl);
          }}
          path={detailsDecl.path}
          pathKind={detailsDecl.pathKind}
          lookup={detailsDecl.lookup}
          provenance={detailsDecl.provenance}
        />
      )}
      {referenceTablesSection}
      {hiddenHints}
    </>
  );
}
